// Core imports
import { Fragment, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useParams, useSearchParams } from 'react-router-dom';

// Component imports
import { Button, Dropdown, Form, Input, Popover } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, SearchOutlined } from '@ant-design/icons';
import VariableDropdown from '../Forms/Fields/RichTextEditor/VariableDropdown';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';

// Misc imports
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { flatten } from '../../utils/object/flatten';
import { AUTOMATED_ANSWERS_SEARCH_BY_TYPES, FLOWS_SEARCH_BY_TYPES, SEARCH_ACTIVITY_TYPES, VARIABLE_TYPES } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { VARIABLE_REGEX } from '../../constants/regex';

const extractVariablesFromString = (string) => {
  return [...string.matchAll(VARIABLE_REGEX)].map((match) => match[0].replace(/\\_/g, '_')); //Remove escape characters before variables with '_'
};

const ActivitiesSearchByInput = ({
  items = [],
  onFilteredItemsReturn = () => {},
  activityType
}) => {
  const { orgId, tenantId } = useParams();
  const { currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const searchByTypePopoverRef = useRef(null);
  const { data: storyVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId });

  const searchByInputRef = useRef(null);

  const SEARCH_BY_TYPES = {
    ...(activityType === SEARCH_ACTIVITY_TYPES.FLOWS && {
      ...FLOWS_SEARCH_BY_TYPES
    }),
    ...(activityType === SEARCH_ACTIVITY_TYPES.AUTOMATED_ANSWERS && {
      ...AUTOMATED_ANSWERS_SEARCH_BY_TYPES
    })
  };

  const [searchValue, setSearchValue] = useState(searchParams?.get('searchTerm') || '');
  const [searchByType, setSearchByType] = useState(searchParams.get('searchBy') || SEARCH_BY_TYPES.NAME);
  const [isSearchByTypePopoverOpen, setIsSearchByTypePopoverOpen] = useState(false);
  const [variableDropdownPosition, setVariableDropdownPosition] = useState({ top: 0, left: 0 });

  const sessionVariables = storyVariables?.filter(variable => variable.type === 'SESSION');

  const onTypeSelectHandler = (type) => {
    setSearchByType(type);
    setIsSearchByTypePopoverOpen(false);

    setTimeout(() => {
      searchByInputRef.current.focus();
    }, 40);
  };

  const searchByMapper = (item, searchByType) => {
    const { content, name, nlpTrainingData } = item || {};
    const nodes = content?.nodes || [];
    const examples = nlpTrainingData?.examples?.[currentBotLanguage] || nlpTrainingData?.examples?.[primaryBotLanguage] || [];

    switch (searchByType) {
      case SEARCH_BY_TYPES.NAME: {
        if(name?.[currentBotLanguage]) {
          return { name: name?.[currentBotLanguage] };
        }

        return { name: name?.[primaryBotLanguage] };
      }

      case SEARCH_BY_TYPES.CONTENT: {
        const mappedNodes = nodes.map(node => {

          if(node?.label?.[currentBotLanguage]) {
            return { label: node.label?.[currentBotLanguage] };
          }

          return { label: node.label?.[primaryBotLanguage] };
        });

        const nlpData = activityType === SEARCH_ACTIVITY_TYPES.AUTOMATED_ANSWERS
          ? { nlpData: nlpTrainingData }
          : {};

        return { nodes: mappedNodes, ...nlpData };
      }

      case SEARCH_BY_TYPES.VARIABLE: {
        const variables = nodes.map(node => ({ key: node?.variable?.key }));
        const contentVariables = nodes.flatMap(node => {
          return extractVariablesFromString(node?.label?.[currentBotLanguage] || node?.label?.[primaryBotLanguage] || '');
        }
        );
        const flowControlVariables = nodes.map(node => {

          if(node?.switchQuery?.[currentBotLanguage]) {
            return { switchQuery: node.switchQuery?.[currentBotLanguage] };
          }

          return { switchQuery: node?.switchQuery?.[primaryBotLanguage] };
        });

        const examplesVariables = examples.flatMap(example =>
          extractVariablesFromString(example)
        );

        return {
          variables,
          contentVariables,
          flowControlVariables,
          examplesVariables
        };
      }

      case SEARCH_BY_TYPES.ANALYTIC_TAG: {
        const analyticsTags = nodes.map(node => node?.analyticTags);
        return { analyticsTags };
      }

      default:
        return item;
    }
  };

  const activitiesSearchByHandler = (searchValue, searchByType) => {
    let filteredItems = [];

    items.forEach(item => {
      const mappedItem = searchByMapper(item, searchByType);
      const searchableString = Object.values(flatten(mappedItem))
        .join(' ')
        .toLowerCase();
      if(searchableString.includes((searchValue || '')?.toLowerCase?.())) {
        filteredItems.push(item);
      }
    });

    return filteredItems;
  };

  useEffect(() => {
    const filteredItems = activitiesSearchByHandler(searchValue, searchByType, activityType);
    onFilteredItemsReturn(filteredItems, searchValue, searchByType);
  }, [searchValue, searchByType, activityType, items]);

  useEffect(() => {
    if (isSearchByTypePopoverOpen && searchByTypePopoverRef.current) {
      const rect = searchByTypePopoverRef.current?.getBoundingClientRect();
      setVariableDropdownPosition({ bottom: rect.bottom });
    }
  }, [isSearchByTypePopoverOpen]);

  return (
    <Input
      ref={searchByInputRef}
      size="large"
      prefix={
        <SearchOutlined
          className="text-secondary"
        />
      }
      placeholder= {t(searchByType, { context: 'search type' })}
      allowClear
      value={searchValue}
      onChange={(event) => setSearchValue(event.target.value)}
      addonAfter = {
        <Fragment>
          <Popover
            open={isSearchByTypePopoverOpen}
            onOpenChange={setIsSearchByTypePopoverOpen}
            placement="bottom"
            trigger="click"
            overlayStyle={{ maxWidth: 260 }}
            content={(
              <Form
                layout="vertical"
                form={form}
                name="filterOptions"
                initialValues={{
                  searchBy: !!searchParams.get('searchBy')
                }}
              >
                {
                  Object.values(SEARCH_BY_TYPES).map(type => (
                    <Fragment>
                      {
                        type === SEARCH_BY_TYPES.VARIABLE ? (
                          <Dropdown
                            overlay={isSearchByTypePopoverOpen ? (
                              <VariableDropdown
                                selectedVariable={form.getFieldValue('searchVariable')}
                                showActivity={false}
                                storyVariables={sessionVariables}
                                onAfterAdd={(variable) => {
                                  onTypeSelectHandler(type);
                                  setSearchValue(variable);
                                }}
                                includedVariableTypes={[VARIABLE_TYPES.SESSION]}
                                truncateCount={0}
                              />
                            ) : <Fragment/>}
                            // https://github.com/ant-design/ant-design/issues/11942#issuecomment-417195388
                            align={{ offset: [variableDropdownPosition.bottom + 32, -32] }}
                          >
                            <Button
                              type="text"
                              block
                              className={classNames('fw-400 text-left', {
                                'bg-body': searchByType === type
                              })}
                              onClick={() => {
                                onTypeSelectHandler(type);
                              }}
                            >
                              {t(type, { context: 'search type' })}
                              <ArrowRightIcon className="right-0 position-absolute mt-1 mr-1 text-link text-strong" />
                            </Button>
                          </Dropdown>
                        ) : (
                          <Button
                            type="text"
                            block
                            className={classNames('fw-400 text-left mb-1', {
                              'bg-body': searchByType === type
                            })}
                            onClick={() => {
                              onTypeSelectHandler(type);
                            }}
                          >
                            {t(type, { context: 'search type' })}
                          </Button>
                        )
                      }

                    </Fragment>

                  ))
                }
              </Form>
            )}
          >
            <Button
              ref={searchByTypePopoverRef}
              className="text-secondary bg-none"
              type="link"
              icon={isSearchByTypePopoverOpen ? <CaretUpOutlined className="fs-sm" /> : <CaretDownOutlined className="fs-sm" />}
              onClick={() => setIsSearchByTypePopoverOpen(!isSearchByTypePopoverOpen)}
            />
          </Popover>
        </Fragment>
      }
    />
  );
};

ActivitiesSearchByInput.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  onFilteredItemsReturn: PropTypes.func,
  activityType: PropTypes.string
};

export default ActivitiesSearchByInput;
